"use client";

import { useLayoutEffect, useRef, type RefObject } from "react";
import type { ReceiptStage } from "./usePillReceipt";

/** How long the thread takes to grow or fold (the grid-rows transition in the
 *  sheet); the face waits this long before flying, so he lands where the seat
 *  WILL be, and the folded thread stays rendered this long so it can close. */
export const THREAD_MS = 320;

/**
 * One Bash, two seats. The face is absolute in the pill and is translated to
 * whichever seat the state leaves for him: the input line, or the top of the
 * thread. A seat change waits for the thread to grow, then he flies, hops and
 * ripples on landing. Moved out of BashPill verbatim.
 */
export function usePillSeat(
  rootRef: RefObject<HTMLElement | null>,
  flyRef: RefObject<HTMLElement | null>,
  seat: "top" | "line",
  /** He is re-placed whenever these move: the thread's phase, the receipt's stage. */
  phase: string,
  receiptStage: ReceiptStage
): void {
  const seatRef = useRef(seat);
  /** Until when a seat change's flight glides; a clock, not a flag, so a
   *  re-run of the placing effect mid-flight can't strand it. */
  const glideUntilRef = useRef(0);
  /** The latest placer, for the landing: the flight outlives a placing run. */
  const placeRef = useRef<(glide: boolean) => void>(() => {});

  // A seat change is the flight: he hops as he sets off and ripples once he
  // has landed (the thread has finished growing or folding by then). Keyed on
  // the seat alone, so the thread's phase moving mid-flight can't cancel the
  // landing; declared first, so the placing run below already glides.
  useLayoutEffect(() => {
    const fly = flyRef.current;
    if (!fly || seatRef.current === seat) return;
    seatRef.current = seat;
    glideUntilRef.current = Date.now() + THREAD_MS + 200;
    fly.classList.add("bh-pill__fly--hop");
    const land = window.setTimeout(() => {
      placeRef.current(true);
      fly.classList.add("bh-pill__fly--land");
    }, THREAD_MS + 200);
    const done = window.setTimeout(
      () => fly.classList.remove("bh-pill__fly--hop", "bh-pill__fly--land"),
      THREAD_MS + 900
    );
    return () => {
      window.clearTimeout(land);
      window.clearTimeout(done);
    };
  }, [flyRef, seat]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const fly = flyRef.current;
    if (!root || !fly) return;
    const place = (glide: boolean) => {
      const slot = root.querySelector<HTMLElement>(`[data-seat="${seat}"]`);
      if (!slot) return;
      const r = root.getBoundingClientRect();
      const s = slot.getBoundingClientRect();
      const to = `${s.left - r.left}px ${s.top - r.top}px`;
      if (glide) {
        fly.style.translate = to;
        return;
      }
      // Between flights he rides his seat in the same frame. His translate is
      // measured from the pill's top edge, and the pill grows and folds from
      // that edge: a re-pin that transitions threw him up the history and slid
      // him back on every resize (founder 09-24: "it goes crazy").
      fly.style.transition = "none";
      fly.style.translate = to;
      void fly.offsetWidth; // commit the jump before the transition returns
      fly.style.transition = "";
    };
    placeRef.current = place;
    const repin = () => place(Date.now() < glideUntilRef.current);
    // The seat moves whenever the pill changes shape: the thread growing or
    // folding (the pill is anchored at the bottom, so its top edge is what
    // moves), the answer streaming. Keep him pinned to it on every resize: a
    // one-shot measurement left him sitting on the control row once the
    // thread folded under him (founder 09-01: "super messed").
    repin();
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(repin);
    ro?.observe(root);
    window.addEventListener("resize", repin);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", repin);
    };
  }, [rootRef, flyRef, seat, phase, receiptStage]);
}
